import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/utils/axios";
import cls from "./AdminCreateCity.module.scss";

const LANGS = ["en", "si", "ta", "hi"];
const STATUSES = ["open", "closed"];

export default function AdminCreateCity() {
  const navigate = useNavigate();

  const [cityLanguage, setCityLanguage] = useState("en");
  const [cityName, setCityName] = useState("");
  const [cityStatus, setCityStatus] = useState("open");
  const [cityVibe, setCityVibe] = useState("");
  const [cityIntro, setCityIntro] = useState("");
  const [cityDescription, setCityDescription] = useState("");
  const [sortOrder, setSortOrder] = useState(0);
  const [isFeatured, setIsFeatured] = useState(false);
  const [tags, setTags] = useState("");

  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const handleBack = () => {
    navigate(-1);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setSuccess("");

    if (!cityName.trim()) {
      setErr("City name is required.");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        cityLanguage,
        cityName: cityName.trim(),
        cityStatus,
        cityVibe,
        cityIntro,
        cityDescription,
        sortOrder: Number(sortOrder) || 0,
        isFeatured,
        tags: tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      };

      const { data } = await api.post("/v1/admin/cities", payload);
      const id = data?._id;
      setSuccess("City created. Redirecting to details page…");
      navigate(id ? `/admin/cities/single/${id}` : "/admin/cities");
    } catch (e) {
      console.error("Create city failed", e);
      const msg =
        e?.response?.data?.message || e?.message || "Failed to create city.";
      setErr(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={cls.page}>
      {/* HEADER */}
      <div className={cls.header}>
        <button type="button" className={cls.backBtn} onClick={handleBack}>
          ← Back
        </button>
        <h1 className={cls.title}>Create City</h1>
        <p className={cls.subtitle}>
          Create a new city shell. After saving, you can upload images and video
          on the city detail page.
        </p>
      </div>

      {/* META BAR */}
      <div className={cls.metaBar}>
        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Mode</span>
          <span className={cls.metaValue}>Create new city</span>
        </div>
        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Language</span>
          <span className={cls.metaValue}>{cityLanguage.toUpperCase()}</span>
        </div>
        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Status</span>
          <span className={cls.metaValue}>{cityStatus}</span>
        </div>
        <div className={cls.metaItem}>
          <span className={cls.metaLabel}>Featured</span>
          <span className={cls.metaValue}>{isFeatured ? "Yes" : "No"}</span>
        </div>
      </div>

      {err && <div className={cls.error}>⚠ {err}</div>}
      {success && <div className={cls.success}>{success}</div>}

      <div className={cls.layout}>
        {/* LEFT CARD – AVATAR PREVIEW / INFO */}
        <section className={cls.avatarCard}>
          <div className={cls.avatarHeader}>
            <h2 className={cls.cardTitle}>City Images</h2>
            <p className={cls.cardSubtitle}>
              When the city is created, you can upload the avatar, thumb, cover,
              and Noty images on the detail page.
            </p>
          </div>

          <div className={cls.avatarPreviewWrap}>
            <div className={cls.avatarGlow}>
              <div className={cls.avatarFallback}>No avatar yet</div>
            </div>
            <p className={cls.avatarMeta}>
              Image uploads become available after this city is saved.
            </p>
          </div>

          <div className={cls.imagesGrid}>
            <div className={cls.imageSlot}>
              <p className={cls.imageLabel}>Avatar Thumb (480.webp)</p>
              <div className={cls.imagePreviewMini}>
                <div className={cls.miniFallback}>Pending</div>
              </div>
              <p className={cls.imageHint}>
                Used for small cards and lists across the app.
              </p>
            </div>

            <div className={cls.imageSlot}>
              <p className={cls.imageLabel}>Cover Image</p>
              <div className={cls.imagePreviewMini}>
                <div className={cls.miniFallback}>Pending</div>
              </div>
              <p className={cls.imageHint}>
                Large hero banner when members enter this city.
              </p>
            </div>

            <div className={cls.imageSlot}>
              <p className={cls.imageLabel}>Mr Noty Image</p>
              <div className={cls.imagePreviewMini}>
                <div className={cls.miniFallback}>Pending</div>
              </div>
              <p className={cls.imageHint}>
                Character art for the city&apos;s male persona.
              </p>
            </div>

            <div className={cls.imageSlot}>
              <p className={cls.imageLabel}>Mrs Noty Image</p>
              <div className={cls.imagePreviewMini}>
                <div className={cls.miniFallback}>Pending</div>
              </div>
              <p className={cls.imageHint}>
                Character art for the city&apos;s female persona.
              </p>
            </div>
          </div>
        </section>

        {/* RIGHT CARD – CREATE FORM */}
        <section className={cls.formCard}>
          <div className={cls.formHeader}>
            <h2 className={cls.cardTitle}>City Details</h2>
            <p className={cls.cardSubtitle}>
              Set the base info. You can fine-tune later on the city detail
              screen.
            </p>
          </div>

          <form className={cls.form} onSubmit={onSubmit}>
            <div className={cls.formRow}>
              <div className={cls.field}>
                <label className={cls.label} htmlFor="cityLanguage">
                  Language
                </label>
                <select
                  id="cityLanguage"
                  className={cls.select}
                  value={cityLanguage}
                  onChange={(e) => setCityLanguage(e.target.value)}
                >
                  {LANGS.map((l) => (
                    <option key={l} value={l}>
                      {l.toUpperCase()}
                    </option>
                  ))}
                </select>
              </div>

              <div className={cls.field}>
                <label className={cls.label} htmlFor="cityStatus">
                  Status
                </label>
                <select
                  id="cityStatus"
                  className={cls.select}
                  value={cityStatus}
                  onChange={(e) => setCityStatus(e.target.value)}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className={cls.formRow}>
              <div className={cls.field}>
                <label className={cls.label} htmlFor="cityName">
                  City name
                </label>
                <input
                  id="cityName"
                  type="text"
                  className={cls.input}
                  value={cityName}
                  onChange={(e) => setCityName(e.target.value)}
                  placeholder="Party Pulse City"
                  required
                />
              </div>

              <div className={cls.field}>
                <label className={cls.label} htmlFor="sortOrder">
                  Sort order
                </label>
                <input
                  id="sortOrder"
                  type="number"
                  className={cls.input}
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                />
                <p className={cls.help}>
                  Lower numbers appear earlier in lists.
                </p>
              </div>
            </div>

            <div className={cls.formRow}>
              <div className={cls.field}>
                <label className={cls.label} htmlFor="cityVibe">
                  Vibe / tagline
                </label>
                <input
                  id="cityVibe"
                  type="text"
                  className={cls.input}
                  value={cityVibe}
                  onChange={(e) => setCityVibe(e.target.value)}
                  placeholder="After-dark energy, party-first crowd…"
                />
              </div>

              <div className={cls.field}>
                <label className={cls.label} htmlFor="isFeatured">
                  Featured city
                </label>
                <div className={cls.checkboxRow}>
                  <input
                    id="isFeatured"
                    type="checkbox"
                    checked={isFeatured}
                    onChange={(e) => setIsFeatured(e.target.checked)}
                  />
                  <span className={cls.help}>
                    Show in featured carousels and hero rows.
                  </span>
                </div>
              </div>
            </div>

            <div className={cls.field}>
              <label className={cls.label} htmlFor="cityIntro">
                Intro
              </label>
              <textarea
                id="cityIntro"
                rows={3}
                className={cls.input}
                value={cityIntro}
                onChange={(e) => setCityIntro(e.target.value)}
                placeholder="Short intro shown on the first fold."
              />
            </div>

            <div className={cls.field}>
              <label className={cls.label} htmlFor="cityDescription">
                Description
              </label>
              <textarea
                id="cityDescription"
                rows={4}
                className={cls.input}
                value={cityDescription}
                onChange={(e) => setCityDescription(e.target.value)}
                placeholder="Longer description when someone opens the city."
              />
            </div>

            <div className={cls.field}>
              <label className={cls.label} htmlFor="tags">
                Tags (comma separated)
              </label>
              <input
                id="tags"
                type="text"
                className={cls.input}
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="party, nightlife, music"
              />
            </div>

            <div className={cls.actions}>
              <button
                type="button"
                className={cls.secondaryBtn}
                onClick={handleBack}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className={cls.primaryBtn}
                type="submit"
                disabled={loading}
              >
                {loading ? "Creating…" : "Create City"}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}
